
// src/pages/Subscription.js
import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form, Spinner, Alert } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { userService } from '../api/services/user';

const Subscription = () => {
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [billingPeriod, setBillingPeriod] = useState('month');

    // Fetch current subscription - opraveno pro React Query v5
    const { data: subscription, isLoading, error } = useQuery({
        queryKey: ['subscription'],
        queryFn: () => userService.getSubscription().then(res => res.data)
    });

    // Plans data - usually would come from an API
    const plans = [
        {
            id: 'basic',
            name: 'Basic',
            description: 'Pro jednotlivce',
            monthlyPrice: 199,
            yearlyPrice: 1990,
            features: [
                'Monitoring až 5 webů',
                'Kontrola dostupnosti každou hodinu',
                'Základní SEO monitoring',
                'Email notifikace'
            ],
            limitations: [
                'Bez bezpečnostního skenování',
                'Bez zálohování'
            ]
        },
        {
            id: 'pro',
            name: 'Pro',
            description: 'Pro profesionály a malé agentury',
            monthlyPrice: 499,
            yearlyPrice: 4990,
            features: [
                'Monitoring až 15 webů',
                'Kontrola dostupnosti každých 15 minut',
                'Pokročilý SEO monitoring',
                'Bezpečnostní skenování 1x týdně',
                'Zálohování 1x týdně',
                'Notifikace přes email a SMS'
            ],
            limitations: []
        },
        {
            id: 'agency',
            name: 'Agency',
            description: 'Pro agentury a větší týmy',
            monthlyPrice: 999,
            yearlyPrice: 9990,
            features: [
                'Monitoring až 50 webů',
                'Kontrola dostupnosti každých 5 minut',
                'Kompletní SEO monitoring',
                'Denní bezpečnostní skenování',
                'Denní zálohování',
                'Všechny notifikační kanály',
                'White-label reporty',
                'API přístup'
            ],
            limitations: []
        }
    ];

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Načítání předplatného...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">Došlo k chybě při načítání předplatného.</Alert>;
    }

    const handleSelectPlan = (planId) => {
        setSelectedPlan(planId);

        // Scrolling to payment section
        const paymentSection = document.getElementById('payment-section');
        if (paymentSection) {
            paymentSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const getCurrentPlanPrice = () => {
        const currentPlan = plans.find(plan => plan.id === (subscription?.plan || 'basic'));
        if (!currentPlan) return 0;
        return billingPeriod === 'month' ? currentPlan.monthlyPrice : currentPlan.yearlyPrice;
    };

    const getSelectedPlanPrice = () => {
        if (!selectedPlan) return 0;
        const plan = plans.find(plan => plan.id === selectedPlan);
        if (!plan) return 0;
        return billingPeriod === 'month' ? plan.monthlyPrice : plan.yearlyPrice;
    };

    const getPriceDifference = () => {
        return getSelectedPlanPrice() - getCurrentPlanPrice();
    };

    return (
        <Container fluid className="">
            <h1 className="mb-4 ">Předplatné</h1>

            <Card className="mb-4 shadow-sm">
                <Card.Header className="bg-white">
                    <h5 className="mb-0">Váš aktuální plán</h5>
                </Card.Header>
                <Card.Body className="p-4">
                    <Row className="align-items-center">
                        <Col md={6}>
                            <div className="d-flex align-items-center">
                                <div className="avatar-circle bg-primary me-3">
                                    <i className="fas fa-crown text-white"></i>
                                </div>
                                <div>
                                    <h3 className="mb-0">{subscription?.plan || 'Basic'}</h3>
                                    <p className="mb-0 fs-5">
                                        {subscription?.price || 199} Kč / {subscription?.billingPeriod || 'měsíc'}
                                    </p>
                                </div>
                            </div>
                        </Col>
                        <Col md={6}>
                            {subscription && (
                                <div className="text-md-end mt-3 mt-md-0">
                                    <p className="mb-1">Platnost do: <strong>{new Date(subscription.validUntil).toLocaleDateString()}</strong></p>
                                    <p className="mb-0">Aktivních webů: <strong>{subscription.websitesCount} / {subscription.websitesLimit}</strong></p>
                                </div>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <div className="mb-4 text-center">
                <h3 className="mb-4">Vyberte si plán, který vám vyhovuje</h3>

                <div className="d-flex justify-content-center align-items-center mb-4">
                    <div className="me-3 fs-5">
                        <span className={billingPeriod === 'month' ? 'fw-bold text-primary' : ''}>Měsíčně</span>
                    </div>
                    <Form.Check
                        type="switch"
                        id="billing-toggle"
                        checked={billingPeriod === 'year'}
                        onChange={() => setBillingPeriod(billingPeriod === 'month' ? 'year' : 'month')}
                        className="billing-toggle"
                    />
                    <div className="ms-3 fs-5">
            <span className={billingPeriod === 'year' ? 'fw-bold text-primary' : ''}>
              Ročně <Badge bg="success" pill className="ms-1">-17%</Badge>
            </span>
                    </div>
                </div>
            </div>

            <Row className="mb-5">
                {plans.map(plan => (
                    <Col lg={4} key={plan.id} className="mb-4">
                        <Card
                            className={`h-100 shadow-sm plan-card ${plan.id === (subscription?.plan || 'basic') ? 'border border-2 border-primary' : ''}`}
                        >
                            <Card.Header className="bg-white text-center p-4">
                                {plan.id === (subscription?.plan || 'basic') && (
                                    <Badge bg="primary" className="position-absolute top-0 start-50 translate-middle px-3 py-2">
                                        Aktuální plán
                                    </Badge>
                                )}
                                <h4>{plan.name}</h4>
                                <p className="text-muted mb-3">{plan.description}</p>
                                <h2 className="mb-0">
                                    {billingPeriod === 'month' ? plan.monthlyPrice : plan.yearlyPrice} Kč
                                    <span className="fs-6 text-muted"> / {billingPeriod === 'month' ? 'měsíc' : 'rok'}</span>
                                </h2>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <h6 className="mb-3">Zahrnuje:</h6>
                                <ul className="mb-4 ps-3 features-list">
                                    {plan.features.map((feature, index) => (
                                        <li key={index} className="mb-2">
                                            <i className="fas fa-check text-success me-2"></i> {feature}
                                        </li>
                                    ))}
                                </ul>

                                {plan.limitations.length > 0 && (
                                    <>
                                        <h6 className="mb-3">Nezahrnuje:</h6>
                                        <ul className="text-muted ps-3 limitations-list">
                                            {plan.limitations.map((limitation, index) => (
                                                <li key={index} className="mb-2">
                                                    <i className="fas fa-times text-danger me-2"></i> {limitation}
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                            </Card.Body>
                            <Card.Footer className="bg-white text-center border-top-0 p-4">
                                {plan.id === (subscription?.plan || 'basic') ? (
                                    <Button variant="outline-primary" disabled>Aktuální plán</Button>
                                ) : (
                                    <Button
                                        variant="primary"
                                        onClick={() => handleSelectPlan(plan.id)}
                                        className="btn-lg w-100"
                                    >
                                        <i className="fas fa-check-circle me-2"></i> Vybrat plán
                                    </Button>
                                )}
                            </Card.Footer>
                        </Card>
                    </Col>
                ))}
            </Row>

            {selectedPlan && (
                <div id="payment-section" className="mb-5">
                    <Card className="shadow">
                        <Card.Header className="bg-white">
                            <h4 className="mb-0">Přejít na plán {plans.find(p => p.id === selectedPlan)?.name}</h4>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <Row>
                                <Col lg={5} className="mb-4 mb-lg-0">
                                    <Card className="bg-light border-0 h-100">
                                        <Card.Body className="p-4">
                                            <h5 className="mb-4">Shrnutí objednávky</h5>
                                            <div className="d-flex justify-content-between border-bottom pb-3 mb-3">
                                                <span>Nový plán:</span>
                                                <span className="fw-bold">{getSelectedPlanPrice()} Kč / {billingPeriod === 'month' ? 'měsíc' : 'rok'}</span>
                                            </div>
                                            <div className="d-flex justify-content-between pb-3 mb-3">
                                                <span>Současný plán:</span>
                                                <span>- {getCurrentPlanPrice()} Kč</span>
                                            </div>
                                            {getPriceDifference() > 0 && (
                                                <div className="d-flex justify-content-between fw-bold fs-5 pt-2">
                                                    <span>K doplacení dnes:</span>
                                                    <span className="text-primary">{getPriceDifference()} Kč</span>
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>

                                <Col lg={7}>
                                    <h5 className="mb-4">Způsob platby</h5>
                                    <Form>
                                        <div className="mb-4">
                                            <div className="payment-options">
                                                <Form.Check
                                                    type="radio"
                                                    name="paymentMethod"
                                                    id="payment-card"
                                                    label={<span><i className="fas fa-credit-card me-2"></i>Platební karta</span>}
                                                    defaultChecked
                                                    className="p-3 border rounded mb-2"
                                                />
                                                <Form.Check
                                                    type="radio"
                                                    name="paymentMethod"
                                                    id="payment-paypal"
                                                    label={<span><i className="fab fa-paypal me-2"></i>PayPal</span>}
                                                    className="p-3 border rounded mb-2"
                                                />
                                                <Form.Check
                                                    type="radio"
                                                    name="paymentMethod"
                                                    id="payment-gopay"                          label={<span><i className="fas fa-wallet me-2"></i>GoPay</span>}
                                                    className="p-3 border rounded mb-2"
                                                />
                                            </div>
                                        </div>

                                        <div className="card-form p-4 border rounded mb-4 bg-light">
                                            <h6 className="mb-3">Údaje o platební kartě</h6>
                                            <Row>
                                                <Col xs={12} className="mb-3">
                                                    <Form.Group>
                                                        <Form.Label>Číslo karty</Form.Label>
                                                        <Form.Control type="text" placeholder="1234 5678 9012 3456" />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xs={6} className="mb-3">
                                                    <Form.Group>
                                                        <Form.Label>Platnost</Form.Label>
                                                        <Form.Control type="text" placeholder="MM/RR" />
                                                    </Form.Group>
                                                </Col>
                                                <Col xs={6} className="mb-3">
                                                    <Form.Group>
                                                        <Form.Label>CVV</Form.Label>
                                                        <Form.Control type="text" placeholder="123" />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </div>

                                        <Form.Check
                                            type="checkbox"
                                            id="terms-agreement"
                                            label={<span>Souhlasím s <a href="/terms" target="_blank">podmínkami služby</a></span>}
                                            className="mb-4"
                                        />

                                        <div className="d-flex justify-content-between">
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => setSelectedPlan(null)}
                                            >
                                                <i className="fas fa-times me-2"></i> Zrušit
                                            </Button>
                                            <Button variant="primary" size="lg">
                                                <i className="fas fa-lock me-2"></i> Dokončit platbu
                                            </Button>
                                        </div>
                                    </Form>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </div>
            )}
        </Container>
    );
};

export default Subscription;